'use client';

import { Button, Modal } from '@components/common';
import { useTranslation } from 'next-i18next';
import { InformationCircleIcon, PercentBadgeIcon } from '@heroicons/react/20/solid';

interface ConversionConfirmationDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  isConfirmLoading?: boolean;
  conversionPriceDetails?: ApiSchema<'UpgradePrice'>;
}

export const ConversionConfirmationDialog = (
  props: ConversionConfirmationDialogProps,
) => {
  // Destructure props
  const {
    open, onConfirm, onCancel, isConfirmLoading, conversionPriceDetails,
  } = props;

  const {
    credits = 0,
    euroPrice = 0,
    missingCredits = 0,
    voucherDiscountCredits = 0,
    vacanciesDiscount = false,
  } = conversionPriceDetails || {};

  // Translation
  const { t } = useTranslation('common');

  return (
    <Modal isOpen={open} onClose={onCancel} size="sm">
      <Modal.Header onClose={onCancel}>
        <h5 className="text-md">{t('my-jobs.card.actions.conversion')}</h5>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-2 text-left font-thin">
          {t('global.action.conversion.details')}
        </p>


        <div className="my-5 divide-y divide-dashed divide-input border-y border-input px-4 sm:px-5">
          {/* Reposting number */}
          {/* Total in euro */}
          <div className="flex items-center justify-between py-2 font-semibold">
            <span>{t('job.add.label.total_euro')}</span>
            <span className="text-right text-lg font-bold">
              <span className="flex items-center justify-end gap-2 text-ink">
                {euroPrice || '0'}
              </span>
              <span className="-mt-1 block text-xs font-normal text-ink-light">
                {t('price.info.vat.not.included')}
              </span>
            </span>
          </div>

          {/* Total in credits */}
          <div className="flex items-center justify-between py-3.5 font-semibold">
            <span>{t('job.add.label.total_credits')}</span>
            <span className="flex items-center gap-2.5 text-2xl font-bold text-secondary">
              {credits || '0'}
            </span>
          </div>

          {/* Info messages */}
          {(voucherDiscountCredits > 0 || vacanciesDiscount || missingCredits > 0) && (
            <div className="flex flex-col gap-2 py-2">
              {/* Voucher discount credits */}
              {voucherDiscountCredits > 0 && (
                <div className="flex items-start gap-1.5 rounded bg-secondary/10 p-2">
                  <PercentBadgeIcon className="size-5 shrink-0 text-secondary" />
                  {t('job.add.voucher.discount.credits', { voucherDiscountCredits })}
                </div>
              )}

              {/* Vacancies discount */}
              {vacanciesDiscount && (
                <div className="flex items-start gap-1.5 rounded bg-secondary/10 p-2">
                  <PercentBadgeIcon className="size-5 shrink-0 text-secondary" />
                  {t('hire_bundle.vacancies.benefit')}
                </div>
              )}

              {/* Missing credits */}
              {missingCredits > 0 && (
                <div className="flex items-start gap-1.5 rounded bg-primary-light/10 p-2">
                  <InformationCircleIcon className="size-5 shrink-0 text-primary-light" />
                  {t('job.add.missing.credits', { missingCredits })}
                </div>
              )}

            </div>
          )}
        </div>


      </Modal.Body>
      <Modal.Footer className="flex justify-end gap-2">
        <Button size="sm" onClick={onConfirm} color="secondary" isLoading={isConfirmLoading} disabled={isConfirmLoading}>
          {t('global.action.conversion.activate')}
        </Button>
        <Button onClick={onCancel} color="light" size="sm">
          {t('global.action.cancel')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
